import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
// import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { debounceTime } from 'rxjs/operators';
import { FilterItem, GenericTableHeaderOptions, RefreshData } from './generic-table-header-options.model';
import { SharedToolsService } from '../../tools/tools.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { MatButtonModule } from '@angular/material/button';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { ToolsModule } from '../../../../hip/tools/src';
@Component({
  selector: 'app-generic-table-header',
  templateUrl: './generic-table-header.component.html',
  standalone: true,
  styleUrls: ['./generic-table-header.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, YearPickerComponent, MonthPickerComponent, DateTimePickerComponent, ToolsModule],
})
export class GenericTableHeaderComponent implements OnInit, OnChanges {
  @Input({ required: true }) config: GenericTableHeaderOptions;
  @Input() savedSearch: string | null;
  @Input() savedFilters: { [key: string]: any } = {};

  @Output() newItemAdded = new EventEmitter<string>();
  @Output() refreshEvent = new EventEmitter<RefreshData>();
  @Output() exportPressed = new EventEmitter<RefreshData>();
  @Output() importEvent = new EventEmitter<void>();
  @Output() selectAllPressed = new EventEmitter<boolean>();
  @Output() dateRangeChange = new EventEmitter<any>();
  @Output() dropdownSelected = new EventEmitter<any>();
  @Output() viewPressed = new EventEmitter<any>();
  @Output() clearPressed = new EventEmitter<any>();
  @Output() startDateTimeChange = new EventEmitter<any>();
  @Output() endDateTimeChange = new EventEmitter<any>();
  @Output() filterValueChanged = new EventEmitter<any>();
  searchQuery = new UntypedFormControl('');

  sortValue: string | null;
  sortOrderIsDesc = true;

  startDateForm: UntypedFormControl = new UntypedFormControl();
  endDateForm: UntypedFormControl = new UntypedFormControl();

  sortChange = false;
  showFilters = false;

  enableDateSearch = false;
  selectAll = false;

  filterForms: Object = {};

  searchFiltersLength = 0;
  selectionFiltersLength = 0;

  selectionFilterLoading = false;

  initialLoad = true;

  constructor(private tools: SharedToolsService, private toastService: ToastrService) { }

  ngOnInit(): void {
    this.searchFiltersLength = this.getSearchFilters().length;
    this.selectionFiltersLength = this.getSelectionFilters().length;
    if (this.config?.FilterItems && this.config?.FilterItems.length > 0) {
      this.config?.FilterItems.forEach((elem) => {
        const savedValue = this.savedFilters?.[elem.Name];
        this.filterForms[elem.Name] = new UntypedFormControl(
          savedValue !== undefined ? savedValue : elem.DefaultValue
        );
      });
    }
    if (this.config?.ToggleFilters && this.config?.ToggleFilters.length > 0) {
      this.config?.ToggleFilters.forEach((elem) => {
        this.filterForms[elem.Name] = new UntypedFormControl(elem.DefaultValue ? elem.DefaultValue : false);
      });
    }

    if (this.config?.DynamicFilterUpdateFn) {
      this.dynamicallyUpdateFilters();
    }
    if (this.config?.DateItem) {
      this.enableDateSearch = true;
    }
    if (this.config?.DateItem && this.config?.DateItem.StartDate) {
      this.startDateForm.setValue(new Date(this.config?.DateItem.StartDate));
    }
    if (this.config?.DateItem && !this.config?.DateItem.Singular && this.config?.DateItem.EndDate) {
      this.endDateForm.setValue(new Date(this.config?.DateItem.EndDate));
    }
    if (this.savedSearch != null) {
      this.searchQuery.setValue(this.savedSearch);
    }
    if (this.config?.DefaultSort) {
      this.sortValue = this.config?.DefaultSort;
    }
    if (this.config?.DefaultDesc != null) {
      this.sortOrderIsDesc = this.config?.DefaultDesc;
    }

    Object.entries(this.filterForms).forEach(([key, value]) => {
      value.valueChanges.pipe(debounceTime(150)).subscribe((val) => {
        this.filterValueChanged.emit({
          Name: key,
          value: val,
        });
      });
    });
  }

  ngOnChanges(): void {
    if (!this.initialLoad) {
      this.ngOnInit();
    }
    this.initialLoad = false;
    this.sortChange = false;
    this.showFilters = false;
    this.selectAll = false;
  }

  clearSearch(): void {
    this.searchQuery.setValue('');
    this.sortOrderIsDesc = false;
    this.submitRefresh();
  }

  selectAllPress(): void {
    this.selectAll = !this.selectAll;
    this.selectAllPressed.emit(this.selectAll);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleDateSearch(): void {
    this.enableDateSearch = !this.enableDateSearch;
  }

  submitRefresh(): void {
    const refreshData: RefreshData = {
      Search: this.searchQuery.value == '' ? null : this.searchQuery.value,
      Sort: this.sortValue,
      Desc: this.sortOrderIsDesc,
      Filters: Object.entries(this.filterForms)
        .map((elem) => {
          if (elem[1].valid == null) {
            return null;
          }
          return {
            Name: elem[0],
            value: elem[1].value,
          };
        })
        .filter((elem) => elem != null),
      FromDate: this.enableDateSearch && this.startDateForm.value ? this.tools.FormatStartDate(this.startDateForm.value) : null,
      ToDate: !this.enableDateSearch || this.config?.DateItem?.Singular || !this.endDateForm.value ? null : this.tools.FormatEndDate(this.endDateForm.value),
    };
    this.refreshEvent.emit(refreshData);
  }

  applyFilters(): void {
    this.showFilters = !this.showFilters;
    this.submitRefresh();
  }

  exportPress(): void {
    if (this.config?.DateRequired && (!this.startDateForm.valid || this.startDateForm.value == null || !this.enableDateSearch)) {
      this.toastService.info('', 'Enter a valid date.', { timeOut: 2000 });
      return;
    }
    const refreshData: RefreshData = {
      Search: this.searchQuery.value == '' ? null : this.searchQuery.value,
      Sort: this.sortValue,
      Desc: this.sortOrderIsDesc,
      Filters: Object.entries(this.filterForms)
        .map((elem) => {
          if (elem[1].valid == null) {
            return null;
          }
          return {
            Name: elem[0],
            value: elem[1].value,
          };
        })
        .filter((elem) => elem != null),
      FromDate: this.startDateForm?.value ? this.tools.FormatStartDate(this.startDateForm.value) : null,
      ToDate: this.config?.DateItem?.Singular || !this.endDateForm.value ? null : this.tools.FormatEndDate(this.endDateForm.value),
    };
    this.exportPressed.emit(refreshData);
  }

  importPressed(): void {
    this.importEvent.emit();
  }

  sortChanged(): void {
    this.sortChange = true;
  }

  sortDirectionChanged(): void {
    this.sortOrderIsDesc = !this.sortOrderIsDesc;
    this.sortChange = true;
  }
  sortApplied(): void {
    if (!this.sortChange) {
      //clear
      this.sortValue = null;
      this.sortOrderIsDesc = true;
    }
    this.sortChange = false;
    this.submitRefresh();
  }

  datePickerChanged(date: Date): void {
    this.startDateForm.setValue(date);
  }

  clearFilters(): void {
    Object.entries(this.filterForms).forEach(([_, val]) => val.setValue(null));
    this.showFilters = !this.showFilters;
    this.submitRefresh();
    if (this.config?.DynamicFilterUpdateFn) {
      this.dynamicallyUpdateFilters();
    }
  }

  getSearchFilters(): FilterItem[] {
    if (!this.config?.FilterItems) {
      return [];
    }
    return this.config?.FilterItems.filter((elem) => elem.Values == null || elem.Values.length == 0);
  }

  getSelectionFilters(): FilterItem[] {
    if (!this.config?.FilterItems) {
      return [];
    }
    return this.config?.FilterItems.filter((elem) => elem.Values != null && elem.Values.length > 0);
  }

  transformToObjectArray(values: any[] | undefined): { Name: string }[] {
    if (values == undefined) {
      return []
    }
    return values?.map(val => ({ Name: val }));
  }


  dateChangeOccurred(event: any) {
    this.dateRangeChange.emit({
      startDate: this.startDateForm.value,
      endDate: this.endDateForm.value,
    });
  }

  dropdownEmit(event: any, item: any) {
    this.dropdownSelected.emit({
      Label: item.Label,
      Value: event.value,
    });
  }

  dynamicallyUpdateFilters() {
    this.selectionFilterLoading = true;
    if (this.config?.DynamicFilterUpdateFn != null) {
      let selections = this.getSelectionFilters().map((elem) => {
        return { Name: elem.Name, Value: this.filterForms[elem.Name].value };
      });
      this.config?.DynamicFilterUpdateFn(selections).subscribe((data) => {
        this.config.FilterItems = data;
        this.selectionFiltersLength = this.getSelectionFilters().length;
        this.selectionFilterLoading = false;
      });
    }
  }
}
